import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LeadForm } from '../components/forms/LeadForm';
import { 
  Building2, Wrench, Users, DollarSign, Shield, 
  CheckCircle, ArrowRight, ExternalLink, Clock, 
  Phone, FileText, Home, Calculator
} from 'lucide-react';

const services = [
  {
    icon: Users,
    title: 'Tenant Placement',
    description: 'Comprehensive tenant screening including credit checks, background verification, income verification, and rental history review.',
    features: ['Marketing & advertising', 'Property showings', 'Application processing', 'Lease preparation']
  },
  {
    icon: DollarSign,
    title: 'Rent Collection',
    description: 'Timely rent collection with online payment options, late fee enforcement, and detailed financial reporting.',
    features: ['Online tenant portal', 'Automatic payment reminders', 'Late fee management', 'Direct deposit to owners']
  },
  {
    icon: Wrench,
    title: 'Maintenance Coordination',
    description: 'Full maintenance management with 24/7 emergency response, vetted contractors, and preventive maintenance programs.',
    features: ['24/7 emergency line', 'Vetted contractor network', 'Preventive maintenance', 'Cost-effective repairs']
  },
  {
    icon: FileText,
    title: 'Financial Reporting',
    description: 'Detailed monthly and annual financial statements, expense tracking, and tax-ready documentation.',
    features: ['Monthly statements', 'Annual tax documents', 'Expense tracking', 'Owner portal access']
  },
  {
    icon: Shield,
    title: 'Legal Compliance',
    description: 'Stay compliant with local, state, and federal regulations including fair housing laws and landlord-tenant requirements.',
    features: ['Lease compliance', 'Fair housing adherence', 'Eviction processing', 'Legal documentation']
  },
  {
    icon: Building2,
    title: 'Property Inspections',
    description: 'Regular property inspections to ensure tenant compliance, identify maintenance needs, and protect your investment.',
    features: ['Move-in/move-out inspections', 'Periodic inspections', 'Photo documentation', 'Condition reports']
  }
];

const maintenanceServices = [
  'HVAC maintenance and repair',
  'Plumbing services',
  'Electrical repairs',
  'Appliance repair/replacement',
  'Landscaping and lawn care',
  'Pool maintenance',
  'Roof inspections and repairs',
  'Pest control',
  'General handyman services',
  'Emergency repairs (24/7)'
];

const managementTiers = [
  {
    name: 'Tenant Placement Only',
    price: "One-Time Fee",
    description: 'Find and screen tenants, then manage the property yourself.',
    features: ['Marketing & advertising', 'Tenant screening', 'Lease preparation', 'Move-in coordination'],
    highlight: false
  },
  {
    name: 'Full Management',
    price: '% of Monthly Rent',
    description: 'Comprehensive hands-off management for busy investors.',
    features: ['Everything in Tenant Placement', 'Rent collection', 'Maintenance coordination', '24/7 emergency support', 'Financial reporting', 'Legal compliance'],
    highlight: true
  },
  {
    name: 'Portfolio Management',
    price: 'Custom Pricing',
    description: 'Discounted rates for investors with multiple properties.',
    features: ['All Full Management services', 'Dedicated account manager', 'Volume discounts', 'Priority service', 'Portfolio-level reporting'],
    highlight: false
  }
];

export const PropertyManagement = () => {
  return (
    <div data-testid="property-management-page">
      {/* Hero Section */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="pm-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="section-overline">Simple Realty Investment Management (SRIM)</p>
            <h1 className="text-4xl sm:text-5xl font-light text-[#F5F5F0] mt-4 mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Property Management <span className="text-gold-gradient">Made Simple</span>
            </h1>
            <p className="text-lg text-[#808080] max-w-2xl mx-auto">
              Maximize your rental income while minimizing your stress. Our professional property management services handle everything from tenant placement to maintenance.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer">
              <Button className="btn-gold" data-testid="pm-consultation-btn">
                Free Management Consultation <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href="#tenant-portal" className="cursor-pointer">
              <Button variant="outline" className="btn-outline-gold" data-testid="pm-tenant-portal-btn">
                Tenant Portal <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link to="/investors">
              <Button variant="outline" className="btn-outline-gold" data-testid="pm-investors-btn">
                Investment Properties <Building2 className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Tenant Portal Section */}
      <section id="tenant-portal" className="py-16 bg-[#D4AF37]/5 border-y border-[#D4AF37]/20" data-testid="tenant-portal-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="section-overline">For Current Tenants</p>
              <h2 className="section-title mt-2 mb-4">Tenant Portal</h2>
              <p className="text-[#808080] mb-6">
                Current tenants can access the portal to pay rent, submit maintenance requests, view lease documents, and communicate with management.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center text-[#F5F5F0] text-sm">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37] mr-2" />
                  Pay rent online 24/7
                </li>
                <li className="flex items-center text-[#F5F5F0] text-sm">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37] mr-2" />
                  Submit maintenance requests
                </li>
                <li className="flex items-center text-[#F5F5F0] text-sm">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37] mr-2" />
                  View lease documents
                </li>
                <li className="flex items-center text-[#F5F5F0] text-sm">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37] mr-2" />
                  Track payment history
                </li>
              </ul>
              <a 
                href="https://simplerealty.appfolio.com/connect/users/sign_in" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="btn-gold" data-testid="tenant-portal-login-btn">
                  Access Tenant Portal <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <p className="text-[#808080] text-xs mt-3">
                *Portal access provided after lease signing. Contact us if you need assistance.
              </p>
            </div>
            <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-lg p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h3 className="text-xl font-light text-[#F5F5F0] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                Need Help?
              </h3>
              <p className="text-[#808080] text-sm mb-4">
                Having trouble accessing the portal or have questions about your tenancy?
              </p>
              <a href="mailto:tenants@simplerealtyandinvestments.com">
                <Button variant="outline" className="btn-outline-gold">
                  Contact Management
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Breakdown */}
      <section className="py-24 bg-[#111111]" data-testid="pm-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="section-overline">Our Services</p>
            <h2 className="section-title mt-2">Comprehensive Property Management</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div 
                key={service.title} 
                className="card-hover bg-[#0a0a0a] p-6 rounded-lg"
                data-testid={`pm-service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-light text-[#F5F5F0] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                  {service.title}
                </h3>
                <p className="text-[#808080] text-sm mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center text-[#F5F5F0] text-sm">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Maintenance Services */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="maintenance-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-overline">Maintenance Integration</p>
              <h2 className="section-title mt-2 mb-6">Full-Service Maintenance</h2>
              <p className="text-[#808080] leading-relaxed mb-8">
                Our integrated maintenance services ensure your property stays in top condition. We coordinate all repairs and maintenance through our network of vetted, licensed contractors.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {maintenanceServices.map((service) => (
                  <div key={service} className="flex items-center text-[#F5F5F0]">
                    <Wrench className="w-4 h-4 text-[#D4AF37] mr-2 flex-shrink-0" />
                    <span className="text-sm">{service}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-4 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-lg">
                <div className="flex items-center mb-2">
                  <Phone className="w-5 h-5 text-[#D4AF37] mr-2" />
                  <span className="text-[#F5F5F0] font-medium">24/7 Emergency Support</span>
                </div>
                <p className="text-[#808080] text-sm">
                  Emergencies don't wait for business hours. Our team is available around the clock for urgent maintenance issues.
                </p>
              </div>
            </div>
            <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-lg p-8">
              <h3 className="text-2xl font-light text-[#F5F5F0] mb-6" style={{ fontFamily: 'Playfair Display' }}>
                How It Works
              </h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37] text-[#0a0a0a] flex items-center justify-center font-semibold mr-4 flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="text-[#F5F5F0] font-medium">Tenant Reports Issue</h4>
                    <p className="text-[#808080] text-sm">Via online portal, phone, or text message</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37] text-[#0a0a0a] flex items-center justify-center font-semibold mr-4 flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="text-[#F5F5F0] font-medium">We Assess & Dispatch</h4>
                    <p className="text-[#808080] text-sm">Qualified contractor sent based on issue type</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37] text-[#0a0a0a] flex items-center justify-center font-semibold mr-4 flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="text-[#F5F5F0] font-medium">Work Completed</h4>
                    <p className="text-[#808080] text-sm">Issue resolved with photo documentation</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37] text-[#0a0a0a] flex items-center justify-center font-semibold mr-4 flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h4 className="text-[#F5F5F0] font-medium">Owner Updated</h4>
                    <p className="text-[#808080] text-sm">Full report with costs on your statement</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Management Tiers */}
      <section className="py-24 bg-[#111111]" data-testid="management-tiers">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="section-overline">Service Options</p>
            <h2 className="section-title mt-2">Choose Your Management Level</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {managementTiers.map((tier) => (
              <div 
                key={tier.name} 
                className={`rounded-lg p-8 ${
                  tier.highlight 
                    ? 'bg-[#D4AF37]/10 border-2 border-[#D4AF37]' 
                    : 'bg-[#0a0a0a] border border-[#D4AF37]/20'
                }`}
                data-testid={`tier-${tier.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {tier.highlight && (
                  <span className="text-[#D4AF37] text-xs font-semibold uppercase tracking-wider">Most Popular</span>
                )}
                <h3 className="text-xl font-light text-[#F5F5F0] mt-2" style={{ fontFamily: 'Playfair Display' }}>
                  {tier.name}
                </h3>
                <p className="text-[#D4AF37] text-2xl font-light mt-2 mb-4" style={{ fontFamily: 'Playfair Display' }}>
                  {tier.price}
                </p>
                <p className="text-[#808080] text-sm mb-6">{tier.description}</p>
                <ul className="space-y-3">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-center text-[#F5F5F0] text-sm">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-Reference Section */}
      <section className="py-16 bg-[#0a0a0a]" data-testid="pm-crossref">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card-hover bg-[#111111] p-8 rounded-lg">
              <div className="flex items-center mb-4">
                <Calculator className="w-8 h-8 text-[#D4AF37] mr-4" />
                <h3 className="text-xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
                  Need Financing?
                </h3>
              </div>
              <p className="text-[#808080] mb-4">
                Explore our loan programs including DSCR loans perfect for investment properties. Qualify based on rental income, not personal income.
              </p>
              <Link to="/financing">
                <Button variant="outline" className="btn-outline-gold">
                  View Financing Options <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="card-hover bg-[#111111] p-8 rounded-lg">
              <div className="flex items-center mb-4">
                <Home className="w-8 h-8 text-[#D4AF37] mr-4" />
                <h3 className="text-xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
                  Looking for Properties?
                </h3>
              </div>
              <p className="text-[#808080] mb-4">
                Browse investment properties in South Florida. We can help you find, finance, and manage your next investment.
              </p>
              <Link to="/investors">
                <Button variant="outline" className="btn-outline-gold">
                  Investment Properties <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Form Section */}
      <section className="py-24 bg-[#111111]" data-testid="pm-lead-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-overline">Get Started</p>
              <h2 className="section-title mt-2 mb-6">Free Property Analysis</h2>
              <p className="text-[#808080] leading-relaxed mb-6">
                Let us analyze your rental property and show you how much you could be earning. We'll provide a complimentary rental market analysis and management proposal.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center text-[#F5F5F0]">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mr-3" />
                  Free rental market analysis
                </li>
                <li className="flex items-center text-[#F5F5F0]">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mr-3" />
                  Custom management proposal
                </li>
                <li className="flex items-center text-[#F5F5F0]">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mr-3" />
                  No obligation consultation
                </li>
              </ul>
            </div>
            <LeadForm 
              source="property_management"
              title="Property Management Inquiry"
              subtitle="Tell us about your property and management needs."
              buttonText="Request Free Analysis"
              showPropertyInterest={true}
            />
          </div>
        </div>
      </section>
    </div>
  );
};
